/**
 * **The legal macro-move generator** — for the acting combatant, enumerate candidate `(destination, weapon, target)` plans by composing the
 * existing engine seams: `reachableForCombatant` (destinations, already gated on
 * `mayPlayMove`/move budget — the same wall `move_token` enforces), `enumerateActions`
 * (which attack-shaped core actions this combatant actually carries), and
 * {@link checkAttackLegal} (the full non-reaction `request_attack` legality ladder,
 * `./legality.ts`). Every emitted {@link MacroMove} carries the EXACT wire intents
 * (`RequestMove`/`RequestAttack`) a CLI can submit verbatim — nothing here re-shapes or
 * re-derives what the room would accept.
 *
 * **Scope:** one destination + at most one attack per macro-move — no move-splitting, no multi-weapon Barrage, no tech actions (Scan/Lock On/Invade), no
 * reactions. A **Superheavy** weapon's attack leg is a `request_barrage` committing its mount alone (Core p.70: a Superheavy fires only
 * in a Barrage, and fires alone, so the Barrage is that one shot); every other weapon's is a Skirmish `request_attack`. A macro-move with no attack (a pure reposition, including "stay put") is always
 * offered alongside every attack pairing, so the advisor can rank retreating/holding position
 * against shooting.
 */

import {
  TRAMPLING_STATUS,
  WEAPON_SIZE_SUPERHEAVY,
  checkMovePath,
  hasStatus,
  isCharacter,
  isItemDestroyed,
  moveBudgetFor,
  movePathAnchors,
  movementContext,
  reachableForCombatant,
  sideOf,
  weaponInstanceKey,
  worldFor,
  type Cell,
  type Combatant,
  type EncounterState,
} from "@lancer-vtt/domain";
import { actionIdForAttackLike, enumerateActions, mountKeyOf, type LoadedOverlay, SEED_OVERLAY } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";
import type { RequestAttack, RequestBarrage, RequestMove, WireCell } from "@lancer-vtt/shared";
import { anchorPathTo } from "@lancer-vtt/space";
import { checkAttackLegal, checkBarrageLegal } from "./legality";

/** The three built-in melee actions' core id ↔ their synthetic `weapon` ref on the wire. */
const MELEE_ACTION_WEAPON_REFS: Record<string, string> = {
  ram: "act_ram",
  grapple: "act_grapple",
  improvised_attack: "act_improvised_attack",
};

/** A `Cell` and a `WireCell` are structurally identical (`RequestAttack`'s doc) — a named pass-through, not a cast site sprinkled everywhere. */
function toWireCell(cell: Cell): WireCell {
  return cell;
}

/** The attack leg of a {@link MacroMove} — always paired with a destination in the parent. */
export type MacroMoveAttack = {
  /** The weapon's instance key (loadout) or a built-in melee-action ref (`act_ram`/`act_grapple`/`act_improvised_attack`). */
  weapon: string;
  /** Display name, for the advisor's reasoning breakdown. */
  weaponName: string;
  target: { id: string; name: string };
  /** The exact intent a CLI submits verbatim: a Skirmish `RequestAttack`, or a lone-Superheavy `RequestBarrage` (its mount committed alone). */
  intent: RequestAttack | RequestBarrage;
};

/** One legal `(destination, [attack])` plan for the acting combatant — the generator's unit of output. */
export type MacroMove = {
  /** Stable id: `"stay"` or `"move:<cell key>"`, `+attack:<weapon>@<target>` when an attack is paired. */
  id: string;
  actor: string;
  /** The anchor cell this plan ends the move at (equals the actor's current position when `moved` is false). */
  destination: Cell;
  moved: boolean;
  /** Move-budget points spent reaching `destination` (0 when `moved` is false). */
  moveCost: number;
  /** The exact `RequestMove` a CLI submits verbatim, or `null` when staying put. */
  moveIntent: RequestMove | null;
  attack: MacroMoveAttack | null;
  /**
   * Human-readable summary for display/reasoning — cells named by raw `world.key` (`Move to 6,4`),
   * which is what the CLI (`lvtt encounter suggest`) prints.
   *
   * **Mirrored in the browser: `frontend/src/lib/encounter/advisorPanel.ts`'s `planLabel` composes
   * this same sentence with board-style cell names (`Move to G5`) for the advisor panel, because the
   * GM is reading it against a lettered ruler.** Rephrase this label and that mirror must move in
   * lockstep — it won't drift silently: `advisorPanel.test.ts` builds its expectation FROM this
   * string at runtime and reds on any wording change. Don't "fix" that test by re-copying; update
   * the mirror.
   */
  label: string;
};

export type GenerateMacroMovesArgs = {
  state: EncounterState;
  actorId: string;
  compendium: Compendium;
  overlay?: LoadedOverlay;
  /**
   * How many reachable destinations beyond "stay" to keep — candidates are pruned to top-k by
   * cheap positional features. Split half aggressive (closest approach to the nearest hostile) /
   * half defensive (farthest retreat); default 10.
   */
  maxDestinations?: number;
};

/** Clone `state` with `actorId` hypothetically standing at `destination`, having spent `moveCost` move-budget getting there. Used ONLY to re-run the real `checkX` ladder against a not-yet-committed position — never to skip a gate. */
function withHypotheticalPosition(args: { state: EncounterState; actorId: string; destination: Cell; moveCost: number }): EncounterState {
  const { state, actorId, destination, moveCost } = args;
  const actor = state.combatants[actorId];
  if (!actor) return state;
  const patchedActor: Combatant = { ...actor, pos: destination };
  const turn = state.activation.turn;
  const patchedTurn =
    turn.phase === "acting" && turn.actor === actorId
      ? { ...turn, budget: { ...turn.budget, move: Math.max(0, turn.budget.move - moveCost) } }
      : turn;
  return {
    ...state,
    combatants: { ...state.combatants, [actorId]: patchedActor },
    activation: { ...state.activation, turn: patchedTurn },
  };
}

/** Every live hostile combatant (opposite `sideOf`, placed) — the targets a macro-move's attack leg may name. */
function hostileTargets(state: EncounterState, actor: Combatant): Combatant[] {
  const mySide = sideOf(actor);
  return Object.values(state.combatants).filter((c) => c.id !== actor.id && c.pos != null && sideOf(c) !== mySide);
}

/** The weapon refs this combatant can currently attack with: non-destroyed loadout weapons + any granted built-in melee action (`enumerateActions`' `route: "attack"` core descriptors). */
function attackWeaponRefs(args: { actor: Combatant; compendium: Compendium; overlay: LoadedOverlay; state: EncounterState }): { ref: string; name: string; superheavy: boolean }[] {
  const { actor, compendium, overlay, state } = args;
  const refs: { ref: string; name: string; superheavy: boolean }[] = [];
  for (const w of actor.base.weapons ?? []) {
    if (isItemDestroyed(actor.current.resources, weaponInstanceKey(w))) continue;
    refs.push({ ref: weaponInstanceKey(w), name: w.name, superheavy: w.mount === WEAPON_SIZE_SUPERHEAVY });
  }
  const turn = state.activation.turn;
  const budget = turn.phase === "acting" && turn.actor === actor.id ? turn.budget : undefined;
  const descriptors = enumerateActions({ combatant: actor, compendium, overlay, budget, state });
  for (const d of descriptors) {
    if (d.route !== "attack") continue;
    const meleeRef = MELEE_ACTION_WEAPON_REFS[d.id];
    if (meleeRef && actionIdForAttackLike({ weapon: meleeRef, tech: false }) === d.id) refs.push({ ref: meleeRef, name: d.name, superheavy: false });
  }
  return refs;
}

/** Pick up to `k` reachable destinations, split aggressive (closest to the nearest hostile)/defensive (farthest), deduped, `actor.pos` always included by the caller. */
function pruneDestinations(state: EncounterState, actor: Combatant, k: number): Cell[] {
  const reachable = reachableForCombatant(state, actor);
  if (!reachable) return [];
  const world = worldFor(state);
  const hostiles = hostileTargets(state, actor).filter((h) => h.pos != null);
  const scored = [...reachable.values()].map((r) => {
    const dist = hostiles.length === 0 ? 0 : Math.min(...hostiles.map((h) => world.distance(r.cell, h.pos!)));
    return { cell: r.cell, dist };
  });
  const half = Math.max(1, Math.floor(k / 2));
  const aggressive = [...scored].sort((a, b) => a.dist - b.dist).slice(0, half);
  const defensive = [...scored].sort((a, b) => b.dist - a.dist).slice(0, half);
  const seen = new Set<string>();
  const out: Cell[] = [];
  for (const entry of [...aggressive, ...defensive]) {
    const key = world.key(entry.cell);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(entry.cell);
    if (out.length >= k) break;
  }
  return out;
}

/**
 * The route a Trampler's plan commits (`landing-zone-full-automation` Q2). None is needed when the
 * engine's own route, which crosses nobody, reaches `destination`. Otherwise the plan sends the
 * cheapest in-budget route that crosses only hostiles, and tramples them, with its cost. `null` when
 * every such route crosses an ally: the destination is dropped rather than planned as a move the room
 * would refuse. Every other mover needs no route.
 */
function trampleRoute(state: EncounterState, actor: Combatant, destination: Cell): { path?: Cell[]; cost?: number } | null {
  if (!hasStatus(actor.statuses, TRAMPLING_STATUS) || actor.pos == null || movePathAnchors(state, actor, destination) !== null) return {};
  const world = worldFor(state);
  const ctx = movementContext(world, state, { exceptId: actor.id, bulldozeFollows: true });
  const blocked = new Set(ctx.blocked);
  for (const c of Object.values(state.combatants)) {
    if (c.id === actor.id || c.pos == null || !isCharacter(c) || sideOf(c) !== sideOf(actor)) continue;
    for (const cell of world.footprint(c.pos, c.base.size).cells()) blocked.add(world.key(cell));
  }
  const path = anchorPathTo({ space: world, from: actor.pos, dest: destination, size: actor.base.size, budget: moveBudgetFor(state, actor), ctx: { ...ctx, blocked } });
  if (path === null) return null;
  const walked = checkMovePath(state, actor, path);
  return walked.ok ? { path, cost: walked.value.cost } : null;
}

/**
 * Enumerate every legal macro-move for `actorId` — the generator's single entry point.
 * Returns `[]` when it isn't legally free to act (not its activation, Stunned/Immobilized, …).
 */
export function generateMacroMoves(args: GenerateMacroMovesArgs): MacroMove[] {
  const { state, actorId, compendium, overlay = SEED_OVERLAY, maxDestinations = 10 } = args;
  const actor = state.combatants[actorId];
  if (!actor || actor.pos == null) return [];
  const turn = state.activation.turn;
  if (turn.phase !== "acting" || turn.actor !== actorId) return [];

  const world = worldFor(state);
  // `reachableForCombatant` itself returns `null` when the combatant may not PLAY-move right now
  // (not its activation, a status bars it) or has no move budget left — no separate pre-check needed.
  const reachableSet = reachableForCombatant(state, actor);
  const destinations: { cell: Cell; cost: number; path?: Cell[] }[] = [{ cell: actor.pos, cost: 0 }];
  if (reachableSet) {
    for (const cell of pruneDestinations(state, actor, maxDestinations)) {
      if (world.key(cell) === world.key(actor.pos)) continue;
      const route = trampleRoute(state, actor, cell);
      if (route === null) continue;
      const cost = route.cost ?? reachableSet.get(world.key(cell))?.cost ?? 0;
      destinations.push({ cell, cost, ...(route.path !== undefined ? { path: route.path } : {}) });
    }
  }

  const moves: MacroMove[] = [];
  for (const { cell: destination, cost: moveCost, path } of destinations) {
    const moved = world.key(destination) !== world.key(actor.pos);
    const moveIntent: RequestMove | null = moved
      ? { type: "request_move", combatantId: actorId, to: toWireCell(destination), ...(path !== undefined ? { path: path.map(toWireCell) } : {}) }
      : null;
    const destKey = world.key(destination);

    // The move-only plan (no attack) — always offered so the advisor can rank a pure
    // reposition (retreat/hold) against every attack pairing at the same destination.
    moves.push({
      id: moved ? `move:${destKey}` : "stay",
      actor: actorId,
      destination,
      moved,
      moveCost,
      moveIntent,
      attack: null,
      label: moved ? `Move to ${destKey}` : "Hold position",
    });

    const hypState = moved ? withHypotheticalPosition({ state, actorId, destination, moveCost }) : state;
    const targets = hostileTargets(hypState, hypState.combatants[actorId] ?? actor);
    if (targets.length === 0) continue;
    const weapons = attackWeaponRefs({ actor: hypState.combatants[actorId] ?? actor, compendium, overlay, state: hypState });
    for (const weapon of weapons) {
      for (const target of targets) {
        const unitTarget = { kind: "unit" as const, id: target.id };
        let attackIntent: RequestAttack | RequestBarrage;
        let verdict;
        if (weapon.superheavy) {
          const mounts = [mountKeyOf(hypState.combatants[actorId]?.base.mounts, weapon.ref)];
          attackIntent = { type: "request_barrage", attacker: actorId, target: unitTarget, weapon: weapon.ref, mounts, accuracy: 0 };
          verdict = checkBarrageLegal(hypState, attackIntent);
        } else {
          attackIntent = { type: "request_attack", attacker: actorId, target: unitTarget, weapon: weapon.ref, accuracy: 0 };
          verdict = checkAttackLegal(hypState, attackIntent);
        }
        if (!verdict.ok) continue;
        moves.push({
          id: `${moved ? `move:${destKey}` : "stay"}+attack:${weapon.ref}@${target.id}`,
          actor: actorId,
          destination,
          moved,
          moveCost,
          moveIntent,
          attack: { weapon: weapon.ref, weaponName: weapon.name, target: { id: target.id, name: target.name }, intent: attackIntent },
          label: `${moved ? `Move to ${destKey}, ` : ""}attack ${target.name} with ${weapon.name}`,
        });
      }
    }
  }
  return moves;
}
